import React, { useEffect, useRef, useState } from "react";
import { Plus, Pencil, Trash2, X, Upload, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, resolveImg, fmt } from "@/lib/api";

const EMPTY = {
  name: "", slug: "", category: "", price: 0, stock: 0,
  short_description: "", description: "", image_url: "",
  purity: ">99%", molecular_weight: "", sequence: "", cas_number: "", featured: false,
  variants: [],
};

export default function AdminProducts() {
  const [products, setProducts] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const load = () => api.get("/products").then((r) => setProducts(r.data));
  useEffect(() => { load(); }, []);

  const startCreate = () => { setEditing("new"); setForm(EMPTY); };
  const startEdit = (p) => { setEditing(p.id); setForm({ ...p }); };
  const close = () => { setEditing(null); setForm(EMPTY); };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      toast.error("Image must be under 10MB");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/uploads/image", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setForm((f) => ({ ...f, image_url: data.url }));
      toast.success("Image uploaded");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const save = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...form,
        price: parseFloat(form.price),
        stock: parseInt(form.stock, 10),
        variants: (form.variants || [])
          .filter((v) => v.label && v.label.trim())
          .map((v) => ({
            label: v.label.trim(),
            price: parseFloat(v.price) || 0,
            stock: parseInt(v.stock, 10) || 0,
          })),
      };
      if (editing === "new") {
        await api.post("/products", payload);
        toast.success("Product created");
      } else {
        const { id, created_at, ...patch } = payload;
        await api.put(`/products/${editing}`, patch);
        toast.success("Product updated");
      }
      close(); load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save");
    }
  };

  const addVariant = () =>
    setForm((f) => ({ ...f, variants: [...(f.variants || []), { label: "", price: 0, stock: 0 }] }));
  const updateVariant = (idx, field, value) =>
    setForm((f) => ({
      ...f,
      variants: (f.variants || []).map((v, i) => (i === idx ? { ...v, [field]: value } : v)),
    }));
  const removeVariant = (idx) =>
    setForm((f) => ({ ...f, variants: (f.variants || []).filter((_, i) => i !== idx) }));

  const remove = async (id) => {
    if (!window.confirm("Delete this product?")) return;
    await api.delete(`/products/${id}`);
    toast.success("Deleted");
    load();
  };

  return (
    <div data-testid="admin-products">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-medium">Products</h2>
        <button onClick={startCreate} data-testid="new-product-btn" className="inline-flex items-center gap-2 bg-cyan-500 hover:bg-cyan-400 text-slate-950 font-semibold px-4 py-2">
          <Plus size={14} /> New
        </button>
      </div>

      <div className="border border-white/5 divide-y divide-white/5">
        {products.map((p) => {
          const hasVariants = Array.isArray(p.variants) && p.variants.length > 0;
          const summary = hasVariants
            ? `${p.category} · ${p.variants.length} variants · from ${fmt(Math.min(...p.variants.map((v) => Number(v.price))))}`
            : `${p.category} · ${fmt(p.price)} · stock ${p.stock}`;
          return (
            <div key={p.id} className="grid grid-cols-[60px_1fr_auto_auto] gap-4 items-center p-4" data-testid={`product-row-${p.slug}`}>
              <img src={resolveImg(p.image_url)} alt="" className="w-14 h-14 object-cover" />
              <div className="min-w-0">
                <div className="font-medium truncate">{p.name}</div>
                <div className="font-mono text-xs text-slate-500 truncate">{summary}</div>
              </div>
              <button onClick={() => startEdit(p)} data-testid={`edit-${p.slug}`} className="text-slate-400 hover:text-cyan-400 p-2"><Pencil size={14} /></button>
              <button onClick={() => remove(p.id)} data-testid={`delete-${p.slug}`} className="text-slate-400 hover:text-red-400 p-2"><Trash2 size={14} /></button>
            </div>
          );
        })}
      </div>

      {editing && (
        <div className="fixed inset-0 z-50" data-testid="product-form-modal">
          <div className="absolute inset-0 bg-slate-950/80" onClick={close} />
          <div className="absolute right-0 top-0 h-full w-full max-w-2xl bg-slate-950 border-l border-white/10 overflow-auto">
            <div className="p-6 border-b border-white/5 flex justify-between items-center">
              <div className="font-medium">{editing === "new" ? "New product" : "Edit product"}</div>
              <button onClick={close} className="text-slate-400 hover:text-white"><X size={18} /></button>
            </div>
            <form onSubmit={save} className="p-6 space-y-4">
              {/* Image uploader */}
              <div className="border border-white/10 p-4" data-testid="image-uploader">
                <div className="label-mono text-cyan-400 mb-3">Product Image</div>
                <div className="flex items-start gap-4">
                  <div className="w-28 h-28 bg-slate-900 border border-white/10 flex items-center justify-center overflow-hidden flex-shrink-0">
                    {form.image_url ? (
                      <img src={resolveImg(form.image_url)} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <span className="text-xs text-slate-600">No image</span>
                    )}
                  </div>
                  <div className="flex-1 space-y-2">
                    <input
                      ref={fileRef}
                      type="file"
                      accept="image/jpeg,image/png,image/webp,image/gif"
                      onChange={handleUpload}
                      className="hidden"
                      data-testid="image-file-input"
                    />
                    <button
                      type="button"
                      disabled={uploading}
                      onClick={() => fileRef.current?.click()}
                      data-testid="upload-image-btn"
                      className="inline-flex items-center gap-2 bg-cyan-500 hover:bg-cyan-400 disabled:bg-slate-700 text-slate-950 font-semibold px-4 py-2 text-sm w-full sm:w-auto"
                    >
                      {uploading ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
                      {uploading ? "Uploading…" : "Upload from device"}
                    </button>
                    <p className="text-[10px] text-slate-500 leading-relaxed">
                      JPEG, PNG, WEBP or GIF · Max 10MB · On mobile, tap to choose from camera or gallery.
                    </p>
                    <input
                      type="text"
                      placeholder="…or paste an image URL"
                      value={form.image_url}
                      onChange={(e) => setForm({ ...form, image_url: e.target.value })}
                      data-testid="pf-image_url"
                      className="w-full bg-transparent border border-white/10 focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500/30 outline-none px-3 h-9 text-xs"
                    />
                  </div>
                </div>
              </div>

              {[
                ["name", "Name", "text"], ["slug", "Slug", "text"], ["category", "Category", "text"],
                ["price", "Price (AUD)", "number"], ["stock", "Stock", "number"],
                ["short_description", "Short description", "text"],
                ["purity", "Purity", "text"], ["molecular_weight", "MW", "text"],
                ["sequence", "Sequence", "text"], ["cas_number", "CAS #", "text"],
              ].map(([k, l, t]) => (
                <label key={k} className="block">
                  <span className="label-mono text-slate-500">{l}</span>
                  <input
                    required={["name", "slug", "category", "price", "stock", "short_description"].includes(k)}
                    type={t}
                    step={t === "number" ? "any" : undefined}
                    data-testid={`pf-${k}`}
                    disabled={k === "slug" && editing !== "new"}
                    value={form[k] ?? ""}
                    onChange={(e) => setForm({ ...form, [k]: e.target.value })}
                    className="mt-1 w-full bg-transparent border border-white/10 focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500/30 outline-none px-3 h-10 text-sm disabled:opacity-50"
                  />
                </label>
              ))}
              <label className="block">
                <span className="label-mono text-slate-500">Description</span>
                <textarea
                  rows={4}
                  data-testid="pf-description"
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  className="mt-1 w-full bg-transparent border border-white/10 focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500/30 outline-none px-3 py-2 text-sm"
                />
              </label>

              {/* Variants editor */}
              <div className="border border-white/10 p-4" data-testid="variants-editor">
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <div className="label-mono text-cyan-400">Size Variants (optional)</div>
                    <p className="text-[10px] text-slate-500 mt-1 leading-relaxed">
                      Add one row per dose size (e.g. 10mg, 15mg). Leave empty for products with a single fixed size — the Price / Stock fields above will be used instead.
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={addVariant}
                    data-testid="add-variant-btn"
                    className="text-xs label-mono text-cyan-400 border border-cyan-500/30 hover:bg-cyan-500/10 px-3 py-1.5"
                  >
                    + Add size
                  </button>
                </div>
                {(form.variants || []).length === 0 ? (
                  <div className="text-xs text-slate-500 py-3">No variants — single size pricing.</div>
                ) : (
                  <div className="space-y-2">
                    <div className="grid grid-cols-[1fr_1fr_1fr_28px] gap-2 text-[10px] label-mono text-slate-500 px-1">
                      <div>Label (e.g. 10mg)</div>
                      <div>Price (AUD)</div>
                      <div>Stock</div>
                      <div></div>
                    </div>
                    {(form.variants || []).map((v, idx) => (
                      <div key={idx} className="grid grid-cols-[1fr_1fr_1fr_28px] gap-2 items-center" data-testid={`variant-row-${idx}`}>
                        <input
                          type="text"
                          value={v.label}
                          onChange={(e) => updateVariant(idx, "label", e.target.value)}
                          placeholder="10mg"
                          data-testid={`variant-label-${idx}`}
                          className="bg-transparent border border-white/10 focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500/30 outline-none px-2 h-9 text-sm"
                        />
                        <input
                          type="number"
                          step="any"
                          value={v.price}
                          onChange={(e) => updateVariant(idx, "price", e.target.value)}
                          placeholder="50.00"
                          data-testid={`variant-price-${idx}`}
                          className="bg-transparent border border-white/10 focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500/30 outline-none px-2 h-9 text-sm font-mono"
                        />
                        <input
                          type="number"
                          value={v.stock}
                          onChange={(e) => updateVariant(idx, "stock", e.target.value)}
                          placeholder="0"
                          data-testid={`variant-stock-${idx}`}
                          className="bg-transparent border border-white/10 focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500/30 outline-none px-2 h-9 text-sm font-mono"
                        />
                        <button
                          type="button"
                          onClick={() => removeVariant(idx)}
                          data-testid={`variant-remove-${idx}`}
                          className="text-slate-500 hover:text-red-400 flex items-center justify-center h-9"
                        >
                          <X size={14} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  data-testid="pf-featured"
                  checked={form.featured}
                  onChange={(e) => setForm({ ...form, featured: e.target.checked })}
                  className="accent-cyan-500"
                />
                Featured on home page
              </label>
              <div className="pt-4 flex gap-3">
                <button type="submit" data-testid="pf-save" className="flex-1 bg-cyan-500 hover:bg-cyan-400 text-slate-950 font-semibold py-2.5">Save</button>
                <button type="button" onClick={close} className="border border-white/10 px-6 text-slate-300">Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
